import { useEffect, useState } from "react";
import { useTranslation } from "../i18n";
import AdminPageHeader from "../components/admin-page-header";
import OutboundSummary from "../components/admin/OutboundSummary";
import PackingNow from "../components/admin/PackingNow";
import FilterPills from "../components/admin/FilterPills";
import { useWarehouseScope } from "../access/useWarehouseScope";
import { apiFetch } from "../api/apiFetch";
import "../styles/admin-settings.css";

// Admin › Warehouse › Outbound (2026-10-14): the day's loading at the
// conveyor unload point and the pallets that left, for Assistant Team
// Leaders and above.
//
// One warehouse at a time, never mixed (Alex, 2026-10-20): warehouse pills
// across the top, styled like Inbound / Pallets, starting on the warehouse
// selected in the admin switcher. Someone without `cross_warehouse` sees
// only their own warehouse's pill.
const readWarehouse = (): string => {
    try {
        const raw = window.localStorage.getItem("user");
        const parsed = raw ? JSON.parse(raw) : null;
        return typeof parsed?.warehouse === "string" ? parsed.warehouse : "";
    } catch {
        return "";
    }
};

const AdminOutbound = () => {
    const { t } = useTranslation();
    const scope = useWarehouseScope();
    const [warehouse, setWarehouse] = useState<string>(() => readWarehouse());
    const [warehouses, setWarehouses] = useState<string[]>([]);

    // The warehouses that exist (hidden ones left out).
    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const res = await apiFetch(`/settings`, {});
                if (!res.ok) return;
                const rows: Array<{ warehouse: string; hidden_at?: string | null }> = await res.json();
                if (alive) setWarehouses(rows.filter((r) => !r.hidden_at).map((r) => r.warehouse).sort());
            } catch {
                /* the pills just show the current warehouse */
            }
        })();
        return () => { alive = false; };
    }, []);

    // Someone who may not cross warehouses is pinned to their own.
    useEffect(() => {
        if (!scope.canCross && scope.own && warehouse !== scope.own) setWarehouse(scope.own);
    }, [scope.canCross, scope.own, warehouse]);

    const options = scope.limit(warehouses.length > 0 ? warehouses : [warehouse].filter(Boolean));

    return (
        <div className="page-data page-data-wide" style={{ height: "calc(100vh - 56px)", overflow: "auto" }}>
            <AdminPageHeader
                titleKey="outbound_title"
                backTo="/admin/warehouse"
                hideSearch
                crumbs={[
                    { labelKey: "admin_panel", to: "/admin" },
                    { labelKey: "warehouse_menu_title", to: "/admin/warehouse" },
                    { labelKey: "outbound_title" },
                ]}
            />
            <div className="admin-settings-section">
                <FilterPills
                    className="warehouse-admin-pills inbound-admin-warehouse-pills"
                    value={warehouse}
                    onChange={(w) => { if (w) setWarehouse(w); }}
                    options={options.map((w) => ({ value: w, label: w }))}
                    hideAll
                />
                <p className="admin-settings-section-hint">{t("outbound_hint", { warehouse })}</p>
                {/* Who is packing what right now, with a leader Release (desk
                    session). Hides itself when the BE has no sessions. */}
                {warehouse && <PackingNow key={`packing-${warehouse}`} warehouse={warehouse} />}
                {/* Keyed by warehouse: switching starts that warehouse's page
                    fresh (today, no leftover courier / status filters). */}
                {warehouse && <OutboundSummary key={warehouse} warehouse={warehouse} />}
            </div>
        </div>
    );
};

export default AdminOutbound;
